import { FC } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MarketCatalogue } from '../../../app/services/api/orderManagementClient';
import CatalogueItemRow from './CatalogueItemRow';
import { Pill } from '../../../components/shared/Pill';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { EmptyRows } from '../../../components/forms/EmptyRows';

type Props = {
  list: MarketCatalogue[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
};

const CataloguesList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  onClear,
  onCreateNew,
}) => {
  const { t } = useTranslation('catalogues');

  if (totalCount === 0 && !isLoading) {
    return (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('management.catalogues')}
        searchTerm={''}
        onClear={onClear}
        onAdd={onCreateNew}
        addItemText={t('management.createNew')}
        onAddIsCreating={false}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Left: Title and Pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="textMD"
            fontWeight={600}
          >
            {t('management.catalogues')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('management.catalogues')}`} />}
        </Box>
      </Box>
      
      {isLoading ? (
        <TableLoading
          headerHeight={45}
          bodyRowHeight={40}
        />
      ) : (
        <TableContainer>
          <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('management.catalogueName')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('management.instanceName')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('management.marketName')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length > 0 ? (
                <>
                  {list.map((catalogue) => (
                    <CatalogueItemRow
                      key={catalogue.customerGroupId}
                      catalogue={catalogue}
                    />
                  ))}
                  <EmptyRows
                    numberOfCurrentRows={list.length}
                    height={40}
                  />
                </>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    {t('management.noCataloguesFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default CataloguesList;
